import { ESensorMode, ESensorModel, TXYPoint } from '@/types';
import { point, vector } from '@flatten-js/core';
import { Sensor } from '../ScopingEditor/useGetState';
import {
  calculateSensorCoverage,
  getSensorCoverageDimensions,
  clampAngleDegrees,
  DEG_TO_RAD,
  lerp,
  MOUNT_ANGLE,
  RAD_TO_DEG,
  rectToPoints,
} from '../utils/utils';
import type { PhysicalObject } from './types';

export type SensorMount = 'ceiling' | 'wall';

/** Decimal precision for polyline doorline coordinates — matches the panel's NumberInput. */
export const POLYLINE_COORD_DECIMALS = 3;

/**
 * Inclusive lower / upper bounds for a stored polyline coordinate. Tightened in from `[0, 1]`
 * to `[0.001, 0.999]` so vertices never sit exactly on the coverage-box boundary — the
 * count engine treats edge points as ambiguous, and the small inset removes that ambiguity
 * without being visually distinguishable on the floor plan.
 */
export const POLYLINE_COORD_MIN = 0.001;
export const POLYLINE_COORD_MAX = 0.999;

/**
 * Round a polyline coordinate to `POLYLINE_COORD_DECIMALS` decimals. Pure rounding only — no
 * clamp, no NaN guard. Use for boundary-detection candidate generation (e.g. the "+ button"
 * fallback in the panel) where an out-of-range or NaN result is information the caller wants
 * to see. For values stored back into Redux, prefer `clampPolylineCoord`.
 */
export function roundPolylineCoord(value: number): number {
  return Number(value.toFixed(POLYLINE_COORD_DECIMALS));
}

/**
 * Round and clamp a polyline coordinate to `[POLYLINE_COORD_MIN, POLYLINE_COORD_MAX]`. NaN-safe:
 * a NaN input collapses to `POLYLINE_COORD_MIN` so a degenerate projection mid-drag (e.g.
 * wall-mount edge math, hot-reload races) can't write `NaN` into Redux — which would silently
 * invalidate the polyline through `hasPolylineDoorLine` on the next read and make the user's
 * drag appear to "delete" the doorline.
 *
 * Used at every spot that writes a polyline coordinate back to state: canvas vertex drag,
 * panel coord input, panel "+ button" fallback. Idempotent — safe to apply repeatedly.
 */
export function clampPolylineCoord(value: number): number {
  if (Number.isNaN(value)) return POLYLINE_COORD_MIN;
  return Math.max(POLYLINE_COORD_MIN, Math.min(POLYLINE_COORD_MAX, roundPolylineCoord(value)));
}

/**
 * A polyline doorline requires at least 2 points to render a segment. An empty or 1-point array
 * is treated as "no polyline doorline" — callers fall back to the standard straight doorline.
 *
 * `algorithm_config` is a JSON scalar from the backend, so the type annotation is only a
 * compile-time promise. Validate each point is a finite-number 2-tuple here at the system
 * boundary; otherwise a malformed payload (e.g. `[[0.29]]`, nulls, strings) would flow through
 * `projectPointIntoCoverage` as NaN and silently render off-canvas.
 */
export function hasPolylineDoorLine(
  polylineDoorLine: ReadonlyArray<unknown> | null | undefined,
): polylineDoorLine is ReadonlyArray<[number, number]> {
  if (!Array.isArray(polylineDoorLine) || polylineDoorLine.length < 2) return false;
  return polylineDoorLine.every(
    (pt): pt is [number, number] =>
      Array.isArray(pt) && pt.length === 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1]),
  );
}

/**
 * Resolves the effective polyline doorline points for a sensor.
 *
 * Sensors carry two representations of this field:
 * - `algoConfig.polylineDoorLine` — Studio's editable shape; local edits write here.
 * - `algorithmConfig.count.polyline_door_line` — raw backend JSON for sensors that haven't
 *   been resaved through Studio.
 *
 * The Studio path wins when present; otherwise fall back to the backend dump. Returns `null`
 * when neither exists. Callers that need a validated polyline should pair this with
 * `hasPolylineDoorLine`.
 */
export function getPolylineDoorLine(
  sensor: Pick<Sensor, 'algoConfig' | 'algorithmConfig'>,
): [number, number][] | null {
  return (
    sensor.algoConfig?.polylineDoorLine ?? sensor.algorithmConfig?.count?.polyline_door_line ?? null
  );
}

/**
 * Returns IN/OUT label anchor positions for a polyline doorline, or null if the polyline has
 * fewer than 2 points.
 *
 * Anchors are placed at the midpoint of the longest edge, offset perpendicular to that edge by
 * `offset` (world units). Which side reads as IN vs OUT is driven by `headcountReversal`:
 *   - `false` → IN on the left of the walking direction (point order), OUT on the right.
 *   - `true`  → OUT on the left, IN on the right (the swap matches the backend's
 *               `headcount_reversal=1` semantic).
 *
 * Left/right is computed in floor-plan math coords (y-up); the left-perpendicular of the
 * unit direction `(ux, uy)` is `(-uy, ux)`, the right is `(uy, -ux)`. The caller computes
 * `headcountReversal` from `inDirection`/`parallelToDoor` (see `SensorObject.headcountReversal`)
 * — this function is intentionally pure-geometric and does not know about sensor fields.
 */
export function polylineDoorLineLabelAnchors(
  points: TXYPoint[],
  offset: number,
  headcountReversal: boolean,
): { inAnchor: TXYPoint; outAnchor: TXYPoint } | null {
  if (points.length < 2) return null;

  // Pick the longest edge — most visual room for labels and least chance of overlapping the polyline.
  let bestIdx = 0;
  let bestLen = -Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1][0] - points[i][0];
    const dy = points[i + 1][1] - points[i][1];
    const len = Math.hypot(dx, dy);
    if (len > bestLen) {
      bestLen = len;
      bestIdx = i;
    }
  }
  const [ax, ay] = points[bestIdx];
  const [bx, by] = points[bestIdx + 1];
  const midX = (ax + bx) / 2;
  const midY = (ay + by) / 2;
  const len = Math.hypot(bx - ax, by - ay) || 1;
  const ux = (bx - ax) / len;
  const uy = (by - ay) / len;
  const left: TXYPoint = [midX + -uy * offset, midY + ux * offset];
  const right: TXYPoint = [midX + uy * offset, midY + -ux * offset];
  return headcountReversal
    ? { inAnchor: right, outAnchor: left }
    : { inAnchor: left, outAnchor: right };
}
/**
 * Represents a sensor and provides functionality for its behavior and properties.
 * Sensors have a position and rotation, plus a number of properties that define their coverage.
 */
export class SensorObject implements PhysicalObject {
  private _position: TXYPoint;
  private _rotation: number;
  private _height: number;
  private _fov: number;
  private _mount: SensorMount;
  private _doorLine: number | null;
  private _parallelToDoor: boolean | null;
  private _inDirection: number | null;
  private _mode: ESensorMode;
  private _shortenedDoorLine: [number, number] | null;
  private _polylineDoorLine: [number, number][] | null;
  private _model?: ESensorModel;

  constructor(args: {
    position: TXYPoint;
    rotation: number;
    height: number;
    fov: number;
    mount: SensorMount;
    mode: ESensorMode;
    doorLine: number | null;
    parallelToDoor: boolean | null;
    inDirection: number | null;
    shortenedDoorLine: [number, number] | null;
    polylineDoorLine?: [number, number][] | null;
    model?: ESensorModel;
  }) {
    this._position = args.position;
    this._rotation = args.rotation;
    this._height = args.height;
    this._fov = args.fov;
    this._mount = args.mount;
    this._mode = args.mode;
    this._doorLine = args.doorLine;
    this._parallelToDoor = args.parallelToDoor;
    this._inDirection = args.inDirection;
    this._shortenedDoorLine = args.shortenedDoorLine;
    this._polylineDoorLine = args.polylineDoorLine ?? null;
    this._model = args.model;
  }

  static fromSensor(
    sensor: Pick<
      Sensor,
      | 'center'
      | 'orientation'
      | 'height'
      | 'fov'
      | 'doorLine'
      | 'parallelToDoor'
      | 'inDirection'
      | 'mode'
      | 'algoConfig'
      | 'algorithmConfig'
    > &
      Partial<Pick<Sensor, 'model'>>,
  ): SensorObject {
    return new SensorObject({
      position: sensor.center,
      rotation: sensor.orientation?.[1] ?? 0,
      height: sensor.height,
      fov: sensor.fov,
      mount: sensor.orientation?.[0] === -30 ? 'wall' : 'ceiling',
      mode: sensor.mode,
      doorLine: sensor.doorLine ?? null,
      parallelToDoor: sensor.parallelToDoor ?? null,
      inDirection: sensor.inDirection ?? null,
      shortenedDoorLine: sensor.algoConfig?.shortenedDoorline ?? null,
      polylineDoorLine: getPolylineDoorLine(sensor),
      model: sensor.model,
    });
  }

  get center(): TXYPoint {
    return this._position;
  }

  get rotation(): number {
    return this._rotation;
  }

  get facingDirection(): number {
    return clampAngleDegrees(
      180 + this._rotation + (this._parallelToDoor ? 0 : -90) + (this._inDirection === 1 ? 180 : 0),
    );
  }

  get mount(): SensorMount {
    return this._mount;
  }

  get height(): number {
    return this._height;
  }

  get fov(): number {
    return this._fov;
  }

  /**
   * Returns projected polyline doorline points in world coordinates, or null if not configured.
   *
   * Stored coords (`this._polylineDoorLine`) are normalized 0-1 to the sensor coverage box but
   * carry a 90° CCW rotation relative to the sensor's local math frame: stored `[x, y]` maps
   * to local-normalized `[y, x]` (axis swap) before projection. Output points are in
   * world/floor-plan coordinates, ready to render as-is in SVG.
   */
  get polylineDoorLineCoordinates(): {
    points: TXYPoint[];
  } | null {
    const polyline = this._polylineDoorLine;
    if (!hasPolylineDoorLine(polyline) || this._mode !== ESensorMode.TRAFFIC) {
      return null;
    }
    const points = polyline.map(([x, y]) => this.projectPointIntoCoverage([y, x]));
    return { points };
  }

  /**
   * `headcount_reversal` derived from the same `inDirection` and `parallelToDoor` fields the
   * count engine uses. The convention: when the two flags **agree** the polyline is walked
   * with IN on the left of the point order; when they **disagree** OUT is on the left. Drives
   * `polylineDoorLineLabelAnchors`'s side-swap so the rendered IN/OUT labels track real sensor
   * direction instead of being hardcoded to one side.
   *
   * `Boolean(...)` normalizes the mixed source types (`number | null` and `boolean | null`)
   * before strict comparison — null/0/false all collapse to false, 1/true to true — which
   * matches the count-engine's loose-equality semantics without leaving JS `!==` to compare
   * across types.
   */
  get headcountReversal(): boolean {
    return Boolean(this._inDirection) !== Boolean(this._parallelToDoor);
  }

  /** Returns the raw numerical door line value. */
  get doorLine(): number | null {
    return this._doorLine;
  }

  /**
   * Returns start and end coordinates of the door line if this sensor has one.
   * Note that only sensors in traffic mode have door lines.
   */
  get doorLineCoordinates(): [TXYPoint, TXYPoint] | null {
    const { doorLine } = this;
    if (doorLine === null || this._mode !== ESensorMode.TRAFFIC) {
      return null;
    }
    const start = this._shortenedDoorLine?.[0] ?? 0;
    const end = this._shortenedDoorLine?.[1] ?? 1;
    if (this._parallelToDoor) {
      return [
        this.projectPointIntoCoverage([start, doorLine]),
        this.projectPointIntoCoverage([end, doorLine]),
      ];
    }
    return [
      this.projectPointIntoCoverage([1 - doorLine, start]),
      this.projectPointIntoCoverage([1 - doorLine, end]),
    ];
  }

  /** Returns the coordinates of this sensor's coverage. */
  get coverageCoordinates(): TXYPoint[] {
    return rectToPoints(0, 0, 1, 1).map((p) => this.projectPointIntoCoverage(p));
  }

  /**
   * Project a point into wall mount coverage.
   * Documented [here](https://www.notion.so/butlrtech/The-calculation-for-theoretical-effective-coverage-of-wall-mounting-sensors-514261a66dfd4d2095349a366085a576?pvs=4).
   */
  private projectPointIntoWallMountCoverage(p: TXYPoint): TXYPoint {
    const { height, fov, rotation } = this;
    const [px, py] = p;
    const efov =
      2 * Math.atan(calculateSensorCoverage({ height, fov }) / (2 * height)) * RAD_TO_DEG;
    const angle = (MOUNT_ANGLE - efov / 2 + efov * py) * DEG_TO_RAD;
    const y = height * Math.tan(angle);
    const xRange = (2 * height * Math.sin((efov / 2) * DEG_TO_RAD)) / Math.cos(angle);
    const x = lerp(-xRange / 2, xRange / 2, px);
    const result = point([x, y])
      .translate(vector(this.center))
      .rotate(-rotation * DEG_TO_RAD, point(this.center));
    return [result.x, result.y];
  }

  /** Project a point into ceiling coverage by simple linear interpolation. */
  private projectPointIntoCeilingCoverage(p: TXYPoint): TXYPoint {
    const { height, fov, rotation } = this;
    const [px, py] = p;
    const { x: sizeX, y: sizeY } = getSensorCoverageDimensions(this._model, height, fov);

    const result = point([lerp(-sizeX / 2, sizeX / 2, px), lerp(-sizeY / 2, sizeY / 2, py)])
      .translate(vector(this.center))
      .rotate(-rotation * DEG_TO_RAD, point(this.center));
    return [result.x, result.y];
  }

  /** Projects a point into the coverage area. (0, 0) is the bottom left, (1, 1) is the top right. */
  projectPointIntoCoverage(p: TXYPoint): TXYPoint {
    if (this.mount === 'ceiling') {
      return this.projectPointIntoCeilingCoverage(p);
    }
    return this.projectPointIntoWallMountCoverage(p);
  }

  /**
   * Inverse of `projectPointIntoCoverage` — converts a world-space point back into the
   * normalized coverage box (math origin: (0,0) = bottom-left, (1,1) = top-right).
   *
   * Used by the polyline doorline canvas editor to convert dragged cursor positions into the
   * stored coordinate system. The polyline forward path in `polylineDoorLineCoordinates`
   * applies `[x, y] → [y, x]` (axis swap) before projection, so callers must apply the inverse
   * `[px, py] → [py, px]` after this call.
   */
  projectPointFromCoverage(p: TXYPoint): TXYPoint {
    // Undo translate-then-rotate (forward order in projectPointInto*) by reversing both.
    const undone = point(p)
      .rotate(this._rotation * DEG_TO_RAD, point(this.center))
      .translate(vector([-this.center[0], -this.center[1]]));
    const lx = undone.x;
    const ly = undone.y;

    if (this.mount === 'ceiling') {
      const { x: sizeX, y: sizeY } = getSensorCoverageDimensions(
        this._model,
        this._height,
        this._fov,
      );
      return [(lx + sizeX / 2) / sizeX, (ly + sizeY / 2) / sizeY];
    }

    // Wall mount: invert y (height * tan(angle)) then x (depends on cos(angle)).
    const efov =
      2 *
      Math.atan(
        calculateSensorCoverage({ height: this._height, fov: this._fov }) / (2 * this._height),
      ) *
      RAD_TO_DEG;
    const angleRad = Math.atan2(ly, this._height);
    const py = (angleRad * RAD_TO_DEG - MOUNT_ANGLE + efov / 2) / efov;
    const xRange = (2 * this._height * Math.sin((efov / 2) * DEG_TO_RAD)) / Math.cos(angleRad);
    const px = xRange === 0 ? 0.5 : (lx + xRange / 2) / xRange;
    return [px, py];
  }

  rotate(angle: number, center: TXYPoint = this.center): SensorObject {
    const nextPosition = point(this._position).rotate(-(angle * DEG_TO_RAD), point(center));
    const { x, y } = nextPosition;
    this._position = [x, y];
    this._rotation = clampAngleDegrees(this._rotation + angle);
    return this;
  }

  translate(dx: number, dy: number): SensorObject {
    const [x, y] = this._position;
    this._position = [x + dx, y + dy];
    return this;
  }
}
